import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useClinicById } from '@/hooks/useClinicById'
import { NoPermissionView } from '@/components/NoPermissionView'
import { PatientCurrentFlow } from '@/pages/adSupplier/clinics/PatientCurrentFlow'
import {
  useClinicPatientFlowRecords,
  UseClinicPatientFlowRecordsParams,
} from '@/hooks/useClinicPatientFlowRecords'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { format, subDays } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon, RefreshCw } from 'lucide-react'
import { DateRange } from 'react-day-picker'

interface ClinicPatientFlowDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: number
}

export function ClinicPatientFlowDialog({
  open,
  onOpenChange,
  clinicId,
}: ClinicPatientFlowDialogProps) {
  const [activeTab, setActiveTab] = useState('daily')
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 7),
    to: new Date(),
  })
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [timeRange, setTimeRange] = useState('7d')
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  // Fetch clinic information
  const {
    clinic,
    isLoading: isClinicLoading,
    mutate: refreshClinic,
  } = useClinicById(open ? clinicId?.toString() : null)

  // We no longer need to fetch current patient flow as it's handled by PatientCurrentFlow component

  // Prepare daily records parameters
  const dailyRecordsParams = useMemo(() => {
    if (!open) return { clinicId: undefined } as UseClinicPatientFlowRecordsParams
    return {
      clinicId,
      interval: 'daily' as const,
      startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      pageSize: 100, // Get more records for better visualization
    } as UseClinicPatientFlowRecordsParams
  }, [open, clinicId, dateRange.from, dateRange.to])

  // Fetch daily records
  const {
    patientFlowRecords: dailyRecords,
    isLoading: isDailyRecordsLoading,
    isError: dailyError,
    mutate: refreshDailyRecords,
  } = useClinicPatientFlowRecords(dailyRecordsParams)

  // Prepare hourly records parameters (單日查詢，由 selectedDate 驅動)
  const hourlyRecordsParams = useMemo(() => {
    if (!open || !selectedDate) return { clinicId: undefined } as UseClinicPatientFlowRecordsParams
    return {
      clinicId,
      interval: 'hourly' as const,
      date: selectedDate,
    } as UseClinicPatientFlowRecordsParams
  }, [open, clinicId, selectedDate])

  // Fetch hourly records (for selected date)
  const {
    patientFlowRecords: hourlyRecords,
    isLoading: isHourlyRecordsLoading,
    mutate: refreshHourlyRecords,
  } = useClinicPatientFlowRecords(hourlyRecordsParams)

  // Prepare today's hourly records for hero section
  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const todayHourlyParams = useMemo(() => {
    if (!open) return { clinicId: undefined } as UseClinicPatientFlowRecordsParams
    return {
      clinicId,
      interval: 'hourly' as const,
      date: todayStr,
    } as UseClinicPatientFlowRecordsParams
  }, [open, clinicId, todayStr])

  const {
    patientFlowRecords: todayHourlyRecords,
    isLoading: isTodayHourlyLoading,
    isError: todayHourlyError,
    mutate: refreshTodayHourly,
  } = useClinicPatientFlowRecords(todayHourlyParams)

  // 任何一支拿到 403 / PERMISSION_DENIED 就視為無權限
  const isForbidden = [dailyError, todayHourlyError].some(
    (e: { error?: { code?: string } } | undefined) => e?.error?.code === 'PERMISSION_DENIED',
  )

  // 類型守衛函數，用於判斷是否為每日記錄
  const isDailyRecord = (
    record: object,
  ): record is {
    patient_flow_daily_id: number
    clinic_id: number
    date: string
    patient_count: number
    created_at: string
    updated_at: string
  } => {
    return 'date' in record && 'patient_flow_daily_id' in record
  }

  // 類型守衛函數，用於判斷是否為小時記錄
  const isHourlyRecord = (
    record: object,
  ): record is {
    flow_hourly_id: number
    clinic_id: number
    snapshot_hour: string
    patient_count: number
    created_at: string
    updated_at: string
  } => {
    return 'snapshot_hour' in record && 'flow_hourly_id' in record
  }

  // Format data for charts
  const dailyChartData = useMemo(() => {
    if (!dateRange.from || !dateRange.to || !dailyRecords) return []

    // 創建從開始日期到結束日期的所有日期陣列
    const allDates: Date[] = []
    const currentDate = new Date(dateRange.from)
    const endDate = new Date(dateRange.to)

    // 確保日期範圍不超過 90 天，避免生成過多數據
    const maxDays = 90
    let daysCount = 0

    while (currentDate <= endDate && daysCount < maxDays) {
      allDates.push(new Date(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
      daysCount++
    }

    // 將 API 返回的數據轉換為 Map，以便快速查找
    const recordMap = new Map()

    dailyRecords.filter(isDailyRecord).forEach((record) => {
      const dateStr = format(new Date(record.date), 'yyyy-MM-dd')
      recordMap.set(dateStr, record.patient_count)
    })

    // 為每一天創建數據點，如果該天有數據則使用實際數據，否則設為 0
    return allDates
      .map((date) => {
        const dateStr = format(date, 'yyyy-MM-dd')
        const formattedDate = format(date, 'MM/dd')
        return {
          date: formattedDate,
          fullDate: dateStr,
          count: recordMap.has(dateStr) ? recordMap.get(dateStr) : 0,
        }
      })
      .sort((a, b) => {
        const dateA = new Date(a.fullDate)
        const dateB = new Date(b.fullDate)
        return dateA.getTime() - dateB.getTime()
      })
  }, [dateRange.from, dateRange.to, dailyRecords])

  // 今日 hourly chart data（用於 hero section）
  const todayHourlyChartData = useMemo(() => {
    if (!todayHourlyRecords) return []

    const recordMap = new Map<number, number>()
    todayHourlyRecords.filter(isHourlyRecord).forEach((record) => {
      const date = new Date(record.snapshot_hour)
      recordMap.set(date.getUTCHours(), record.patient_count)
    })

    return Array.from({ length: 24 }, (_, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      count: recordMap.get(hour) ?? 0,
    }))
  }, [todayHourlyRecords])

  // 將 hourly API 資料轉換為 24 個時段 slot（00:00~23:00）
  const hourlyChartData = useMemo(() => {
    if (!selectedDate || !hourlyRecords) return []

    // 將 API 返回的數據轉換為 Map，以小時為 key
    const recordMap = new Map<number, number>()

    hourlyRecords.filter(isHourlyRecord).forEach((record) => {
      const date = new Date(record.snapshot_hour)
      const hour = date.getUTCHours()
      recordMap.set(hour, record.patient_count)
    })

    // 建立 24 個時段 slot
    return Array.from({ length: 24 }, (_, hour) => ({
      hour: `${hour.toString().padStart(2, '0')}:00`,
      count: recordMap.get(hour) ?? 0,
    }))
  }, [selectedDate, hourlyRecords])

  // Handle time range selection
  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value)
    setSelectedDate(null) // 切換時間範圍時重置選取日期
    const today = new Date()

    switch (value) {
      case '24h':
        setDateRange({ from: subDays(today, 1), to: today })
        break
      case '7d':
        setDateRange({ from: subDays(today, 7), to: today })
        break
      case '30d':
        setDateRange({ from: subDays(today, 30), to: today })
        break
      case '90d':
        setDateRange({ from: subDays(today, 90), to: today })
        break
    }
  }

  // Refresh data when dialog opens
  useEffect(() => {
    if (open) {
      refreshClinic()
      refreshDailyRecords()
      refreshHourlyRecords()
      refreshTodayHourly()
    }
  }, [open, refreshClinic, refreshDailyRecords, refreshHourlyRecords, refreshTodayHourly])

  // We don't need to refresh data when date range changes anymore
  // The memoized parameters will trigger a refetch automatically

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] max-h-[90vh] flex flex-col overflow-y-auto">
        {/* Header with clinic info */}
        <div className="flex items-start justify-between">
          <div>
            {isClinicLoading ? (
              <Skeleton className="h-8 w-[300px] mb-2" />
            ) : (
              <h2 className="text-2xl font-bold">{clinic?.clinic_name} - 人流統計</h2>
            )}
            {isClinicLoading ? (
              <Skeleton className="h-4 w-[200px]" />
            ) : (
              <p className="text-muted-foreground">
                {clinic?.address} | 聯絡人: {clinic?.manager_name} | 電話: {clinic?.phone}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              refreshClinic()
              refreshDailyRecords()
              refreshHourlyRecords()
              refreshTodayHourly()
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <Separator className="my-4" />

        {isForbidden ? (
          <NoPermissionView
            description="此功能需要廣告供應商統計報表權限，請聯絡管理員開通"
            className="flex-1"
          />
        ) : (
        <>
        {/* Current patient flow hero section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <PatientCurrentFlow clinicId={clinicId} />

          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">今日人流趨勢</CardTitle>
              <CardDescription>今日每小時人流變化</CardDescription>
            </CardHeader>
            <CardContent>
              {isTodayHourlyLoading ? (
                <Skeleton className="h-[200px] w-full" />
              ) : todayHourlyChartData.length === 0 ? (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  暫無今日數據
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                    data={todayHourlyChartData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#8884d8"
                      activeDot={{ r: 8 }}
                      name="人數"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs for daily/hourly data */}
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <div className="w-[400px]">{/* Empty div for spacing */}</div>

            <div className="flex items-center gap-2">
              <Select value={timeRange} onValueChange={handleTimeRangeChange}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="選擇時間範圍" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">過去24小時</SelectItem>
                  <SelectItem value="7d">過去7天</SelectItem>
                  <SelectItem value="30d">過去30天</SelectItem>
                  <SelectItem value="90d">過去90天</SelectItem>
                </SelectContent>
              </Select>

              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="justify-start text-left font-normal w-[240px]"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, 'yyyy-MM-dd')} -{' '}
                          {format(dateRange.to, 'yyyy-MM-dd')}
                        </>
                      ) : (
                        format(dateRange.from, 'yyyy-MM-dd')
                      )
                    ) : (
                      <span>選擇日期範圍</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      if (range?.from && range?.to) {
                        setDateRange(range)
                        setCalendarOpen(false)
                      }
                    }}
                    numberOfMonths={2}
                    locale={zhTW}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="mb-4">
              <TabsTrigger value="daily">每日統計</TabsTrigger>
              <TabsTrigger value="hourly">每小時統計</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-auto">
              <TabsContent value="daily" className="h-full space-y-4">
                {isDailyRecordsLoading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : dailyChartData.length === 0 ? (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    所選時間範圍內暫無數據
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">點選長條圖上的日期查看該日時段人流</p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={dailyChartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        onClick={(state) => {
                          if (state?.activePayload?.[0]?.payload?.fullDate) {
                            setSelectedDate(state.activePayload[0].payload.fullDate)
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" name="人數">
                          {dailyChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.fullDate === selectedDate ? '#6c5ce7' : '#8884d8'}
                              stroke={entry.fullDate === selectedDate ? '#5a4bd1' : undefined}
                              strokeWidth={entry.fullDate === selectedDate ? 2 : 0}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )}

                {/* 逐時人流長條圖（點選日期後顯示） */}
                {selectedDate && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">
                        {selectedDate} 時段人流
                      </CardTitle>
                      <CardDescription>該時段新增人流數</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {isHourlyRecordsLoading ? (
                        <Skeleton className="h-[250px] w-full" />
                      ) : hourlyChartData.length === 0 ? (
                        <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                          該日暫無時段數據
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart
                            data={hourlyChartData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="count" name="人數" fill="#8884d8" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="hourly" className="h-full">
                {!selectedDate ? (
                  <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                    請先在「每日統計」頁籤中點選日期
                  </div>
                ) : isHourlyRecordsLoading ? (
                  <Skeleton className="h-[400px] w-full" />
                ) : hourlyChartData.length === 0 ? (
                  <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                    該日暫無時段數據
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={hourlyChartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="count"
                        name="人數"
                        stroke="#8884d8"
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          {isDailyRecordsLoading ? (
            Array(4)
              .fill(0)
              .map((_, i) => <Skeleton key={i} className="h-24 w-full" />)
          ) : (
            <>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">平均每日人流</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {dailyChartData.length > 0
                      ? Math.round(
                          dailyChartData.reduce((sum, item) => sum + item.count, 0) /
                            dailyChartData.length,
                        )
                      : 0}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">最高日人流</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {dailyChartData.length > 0
                      ? Math.max(...dailyChartData.map((item) => item.count))
                      : 0}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {dailyChartData.length > 0
                      ? dailyChartData.reduce(
                          (max, item) => (item.count > max.count ? item : max),
                          { count: 0, date: '' },
                        ).date
                      : ''}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">今日平均每小時人流</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {(() => {
                      const withData = todayHourlyChartData.filter((item) => item.count > 0)
                      return withData.length > 0
                        ? Math.round(
                            withData.reduce((sum, item) => sum + item.count, 0) / withData.length,
                          )
                        : 0
                    })()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">總計人流</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {dailyChartData.reduce((sum, item) => sum + item.count, 0)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {dateRange.from && dateRange.to
                      ? `${format(dateRange.from, 'yyyy-MM-dd')} 至 ${format(
                          dateRange.to,
                          'yyyy-MM-dd',
                        )}`
                      : ''}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
